
import { useRef } from 'react';

const highlights = [
  {
    title: 'Libertas Academy Woodruff',
    description: 'Awarded Major Planning & Implementation Grant',
    link: 'https://www.libertaswoodruff.education/o/woodruff/article/2323341?fbclid=IwRlRTSAOO3MFleHRuA2FlbQIxMQBzcnRjBmFwcF9pZAo2NjI4NTY4Mzc5AAEeqyWyn0GXnaWC8rzQ2D4jimDouHZkC_XaJWYKn8BGm0LilLHuTPKyzj4v7Ng_aem_gfJ9j23RkyAax6asGK183w',
    image: 'https://static.readdy.ai/image/afa85a1250ffb5ee259f890220a101e9/b1b12c22134b4a263ce0edfbc11d1cd3.jpeg'
  },
  {
    title: 'RISE Entrepreneurial Leadership Academy',
    description: 'Successful SC Charter Approval',
    link: 'https://erskinecharters.org/2025/04/charter-institute-approves-sumter-entrepreneurial-school/',
    image: 'https://static.readdy.ai/image/afa85a1250ffb5ee259f890220a101e9/91c0e74b965ba9aa4cf148cb05580b4b.png'
  },
  {
    title: 'Willie Jeffries Schools of Excellence',
    description: 'Awarded $850K in Grant Funding',
    link: 'https://www.facebook.com/share/v/19bGKhW5kE/?mibextid=wwXIfr',
    image: 'https://static.readdy.ai/image/afa85a1250ffb5ee259f890220a101e9/652bda2c10d1acbf226fc76908330313.jpeg'
  }
];

export default function DifferentiatorSection() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.offsetWidth / 2;
      const newScrollLeft = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="client-highlights" className="py-16 bg-white" aria-labelledby="highlights-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-gray-600 tracking-wider uppercase mb-3">
            RECENT HIGHLIGHTS
          </p>
          <h2 id="highlights-heading" className="text-4xl font-light text-gray-900">
            Real <span className="font-bold">Results.</span> Real <span className="font-bold">Impact.</span>
          </h2>
        </div>

        <div className="relative">
          {/* Scroll Container */}
          <div 
            ref={scrollContainerRef}
            className="overflow-x-auto scrollbar-hide snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            role="region"
            aria-label="Client success stories carousel"
            tabIndex={0}
          >
            <div className="flex gap-8 lg:grid lg:grid-cols-3" role="list">
              {highlights.map((highlight, index) => (
                <article
                  key={index}
                  className="flex-shrink-0 w-[85vw] sm:w-[70vw] lg:w-auto bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300 snap-start"
                  role="listitem"
                >
                  <div className="w-full h-64 overflow-hidden">
                    <img
                      src={highlight.image}
                      alt={`${highlight.title} - ${highlight.description}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-6">
                    <div className="mb-6">
                      <h3 className="text-xl font-medium text-gray-900 mb-3">
                        {highlight.title}
                      </h3>
                      <p className="text-base text-gray-700 font-light">
                        {highlight.description}
                      </p>
                    </div>
                    <a
                      href={highlight.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-gray-900 text-white px-6 py-3 rounded hover:bg-gray-800 transition-colors duration-200 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-4 focus:ring-gray-900 focus:ring-opacity-50"
                      aria-label={`View details about ${highlight.title}`}
                    >
                      View Details
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Navigation Buttons - Only visible on mobile/tablet */}
          <button
            onClick={() => scroll('left')}
            className="lg:hidden absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer z-10 focus:outline-none focus:ring-4 focus:ring-gray-900 focus:ring-opacity-50"
            aria-label="Scroll to previous client highlight"
          >
            <i className="ri-arrow-left-s-line text-2xl text-gray-900" aria-hidden="true"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            className="lg:hidden absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white shadow-lg rounded-full w-12 h-12 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer z-10 focus:outline-none focus:ring-4 focus:ring-gray-900 focus:ring-opacity-50"
            aria-label="Scroll to next client highlight"
          >
            <i className="ri-arrow-right-s-line text-2xl text-gray-900" aria-hidden="true"></i>
          </button>

          {/* Scroll Indicators - Only visible on mobile/tablet */}
          <div className="lg:hidden flex justify-center gap-2 mt-8" role="tablist" aria-label="Client highlights navigation">
            {highlights.map((_, index) => (
              <div
                key={index}
                className="w-2 h-2 rounded-full bg-gray-300"
                role="tab"
                aria-label={`Go to highlight ${index + 1}`}
              ></div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}
